/**
 * Service Settings Types
 * 
 * Type definitions for service settings used in simulations.
 * Service settings define operational parameters including time windows,
 * processing times, capacities, and other service-specific configurations.
 */

/**
 * Service flags that control various service features
 */
export interface ServiceFlags {
  hubsEnabled: boolean;
  geofencesEnabled: boolean;
  breaksEnabled: boolean;
  zonesEnabled: boolean;
  isHubToHub: boolean;
}

/**
 * Capacity type definition
 */
export interface CapacityType {
  name: string;
  iconName: string | null;
  displayOrder: number;
}

/**
 * Capacity configuration for a service
 */
export interface Capacity {
  id: string;
  onceOffTime: number; // Processing time in seconds
  timePerUnit: number; // Additional time per unit in seconds
  movable: boolean;
  isDependant: boolean;
  type: CapacityType;
}

/**
 * Processing time configuration for different seat types
 */
export interface ProcessingTime {
  seatType: string; // e.g., 'standard', 'wheelchair'
  processingTimeSeconds: number;
}

/**
 * Time window solution (operating hours)
 */
export interface TimeWindowSolution {
  start: string; // Time in HH:mm format
  end: string; // Time in HH:mm format
}

/**
 * Time window settings for service calculations
 */
export interface TimeWindowSettings {
  slope: number; // Slope for time window calculation
  intercept: number; // Intercept for time window calculation (in seconds)
  fixedWindow: number; // Fixed window size in seconds
}

/**
 * Metadata preserved when importing from UAT/Production
 */
export interface UATMetadata {
  importedAt: number; // Timestamp when imported
  importedFrom: string; // Environment (UAT/Production)
  productId: string; // Product/network ID
  serviceDate: string; // Service date when imported (YYYY-MM-DD)
  originalId?: string; // Original UAT service ID
  originalName?: string; // Original UAT service name
  originalData?: any; // Preserve all original fields for future reference
}

/**
 * Main Service Settings interface
 * 
 * Represents a complete service configuration that can be used in simulations.
 * Services can be created manually, downloaded from UAT/Production, or created
 * from simulation modal configurations.
 */
export interface Service {
  id: string; // Unique identifier
  name: string; // User-friendly name
  description?: string; // Optional description
  serviceType: string; // e.g., 'doorToDoor', 'hubToHub'
  serviceFlags: ServiceFlags; // Feature flags
  timezone: string; // Timezone (e.g., 'America/Sao_Paulo') - automatically derived from network settings
  rideLockTimeOffset: number; // Ride lock time offset in seconds
  maxCompanions: number; // Maximum number of companions per ride
  networkId?: string; // Network ID (optional, for downloaded services)
  
  // Capacity and processing configuration
  capacities: Capacity[]; // Available capacities
  processingTimes: ProcessingTime[]; // Processing times by seat type
  onceOffProcessingTime: number; // Base processing time in seconds
  
  // Time window configuration
  timeWindowSettings: TimeWindowSettings; // Time window calculation parameters
  maxTripTimeMinutes: number; // Maximum trip time in minutes
  solutions?: TimeWindowSolution[]; // Operating hours/solutions (optional)
  
  // Travel time configuration
  travelTimeMultiplier: number; // Base travel time multiplier
  useVariableMultipliers: boolean; // Whether to use hourly multipliers
  hourlyMultipliers?: number[]; // 24-hour array of multipliers (optional)
  
  // Geographic data (optional, for downloaded services)
  geofences?: any[]; // Geofences associated with service
  hubs?: any[]; // Hubs associated with service
  
  // UAT metadata (optional, for downloaded services)
  uatMetadata?: UATMetadata; // Preserved metadata from UAT/Production import
}

/**
 * Default service flags (all disabled)
 */
export const defaultServiceFlags: ServiceFlags = {
  hubsEnabled: false,
  geofencesEnabled: false,
  breaksEnabled: false,
  zonesEnabled: false,
  isHubToHub: false,
};

/**
 * Default time window settings
 */
export const defaultTimeWindowSettings: TimeWindowSettings = {
  slope: 1.7,
  intercept: 600,
  fixedWindow: 900,
};

/**
 * Default processing times
 */
export const defaultProcessingTimes: ProcessingTime[] = [
  { seatType: 'standard', processingTimeSeconds: 30 },
  { seatType: 'wheelchair', processingTimeSeconds: 90 },
];

/**
 * Default capacities
 */
export const defaultCapacities: Capacity[] = [
  {
    id: 'default-standard',
    onceOffTime: 30,
    timePerUnit: 0,
    movable: true,
    isDependant: false,
    type: {
      name: 'standard',
      iconName: null,
      displayOrder: 1,
    },
  },
  {
    id: 'default-wheelchair',
    onceOffTime: 180,
    timePerUnit: 0,
    movable: false,
    isDependant: false,
    type: {
      name: 'wheelchair',
      iconName: null,
      displayOrder: 2,
    },
  },
];

/**
 * Create an empty service with default values
 * 
 * @param name - Optional name for the service (defaults to "New Service")
 * @returns A new Service object with default values
 */
export function createEmptyService(name: string = 'New Service'): Service {
  const serviceId = `service-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  return {
    id: serviceId,
    name,
    description: '',
    serviceType: 'doorToDoor',
    serviceFlags: { ...defaultServiceFlags },
    timezone: 'UTC', // Will be updated from network settings when available
    rideLockTimeOffset: 45,
    maxCompanions: 6,
    capacities: [...defaultCapacities],
    processingTimes: [...defaultProcessingTimes],
    onceOffProcessingTime: 15,
    timeWindowSettings: { ...defaultTimeWindowSettings },
    maxTripTimeMinutes: 60, // Default 1 hour
    travelTimeMultiplier: 1.1,
    useVariableMultipliers: false,
    hourlyMultipliers: undefined,
  };
}

